import { useState } from 'react';
import { Link } from 'react-router-dom';
import { COMPANY_INFO } from '../data/staticData';
import ContactForm from '../components/ContactForm';

export default function AsbestosPage() {
  const [openFaq, setOpenFaq] = useState([0]); // First one open by default

  const faqs = [
    {
      q: 'Må jeg selv fjerne et asbesttag?',
      a: 'Nej. Den 1. januar 2025 trådte en ny autorisationsordning i kraft, som betyder, at private ikke længere selv må fjerne asbest. Ifølge lovgivningen må nedrivning og sanering af asbestholdige materialer som fx eternittage kun udføres af virksomheder, der har autorisation fra Arbejdstilsynet.'
    },
    {
      q: 'Hvad koster det at få fjernet et asbesttag?',
      a: 'Fjernelse af et asbesttag er generelt dyrere end nedtagning af almindelige tagplader. Det skyldes, at arbejdet kræver autorisation, særligt udstyr, skærpede sikkerhedsprocedurer og korrekt bortskaffelse af det farlige affald. Prisen afhænger af flere forhold, blandt andet tagets størrelse og opgavens kompleksitet. Hos Dansk Tagbyg giver vi gerne et uforpligtende tilbud, så du ved, hvad du kan forvente.'
    },
    {
      q: 'Hvordan ved jeg, om mit tag indeholder asbest?',
      a: 'Eternittage, der er produceret før 1990, indeholder næsten altid asbest. Det gælder især bølgeeternitplader af typen B5 og B6. Er taget fra 1990 eller senere, er det typisk uden asbest, men der findes undtagelser. Derfor kan det være nødvendigt at få lavet en analyse af taget, hvis du er i tvivl. Hos Dansk Tagbyg hjælper vi med at vurdere, om dit tag indeholder asbest og rådgiver dig om den bedste og mest sikre løsning for udskiftning.'
    },
    {
      q: 'Må jeg selv køre asbestaffald på genbrugspladsen?',
      a: 'Nej. Det er kun virksomheder med autorisation, der må bortskaffe asbestaffald.'
    },
    {
      q: 'Kan jeg få tilskud til at få fjernet mit asbesttag?',
      a: 'Nogle kommuner tilbyder tilskud som en del af deres miljø- og sundhedsprogrammer, og der findes også regionale støtteordninger, der inkluderer støtte til asbestsanering. Vi anbefaler, at du kontakter din kommune for at høre om eventuelle tilskudsmuligheder. Du er også altid velkommen til at ringe til os, så hjælper vi dig gerne videre.'
    },
    {
      q: 'Hvad betyder det, at I er autoriseret til at fjerne asbest?',
      a: 'Det betyder, at vi hos Dansk Tagbyg har opnået den nødvendige autorisation fra Arbejdstilsynet og er godkendt til at udføre nedrivning og sanering af asbestholdige materialer som fx eternittage – og at vores medarbejdere har gennemført den lovpligtige uddannelse i asbestarbejde. Du kan derfor trygt overlade opgaven til os.'
    },
    {
      q: 'Hvad sker der, hvis man ikke bruger en autoriseret virksomhed?',
      a: 'Hvis virksomheder uden autorisation eller private udfører nedrivning af asbest, kan det medføre bødestraf. For virksomheder er standardbøden 30.000 kr., men den kan stige afhængigt af overtrædelsens alvor. For private er standardbøden 15.000 kr.'
    },
    {
      q: 'Hvad er asbest egentlig – og hvorfor er det farligt?',
      a: 'Asbest er et sundhedsskadeligt materiale, der tidligere blev brugt i fx tagplader. Hvis det håndteres forkert, kan støvet skade lungerne og medføre alvorlige sygdomme som lunge- og lungehindekræft.'
    }
  ];

  const toggleFaq = (idx) => {
    setOpenFaq((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16 border-b border-stone-200">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-8 space-y-6">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-stone-900 tracking-tight uppercase">
              Nedtagning af asbesttag? Vi er asbest-autoriseret og klar til at hjælpe dig
            </h1>
            <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
              <p>
                Siden 1. januar 2025 har det været et krav i arbejdsmiljølovgivningen, at nedrivning og sanering af asbestholdige materialer som fx eternittage kun må udføres af virksomheder, der er autoriseret af Arbejdstilsynet. De nye autorisationskrav er lavet for at beskytte håndværkerne mod de sundhedsskadelige fibre, der frigives, når asbestholdige materialer beskadiges eller fjernes.
              </p>
              <p className="font-semibold text-stone-900">
                Hos Dansk Tagbyg har vi den lovpligtige autorisation og en fast procedure for sikker og korrekt håndtering af asbest.
              </p>
            </div>

            {/* Quick Links */}
            <div className="pt-4 border-t border-stone-200">
              <p className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">
                Klik og kom længere ned på siden:
              </p>
              <div className="flex flex-wrap gap-4 text-sm font-bold">
                <a href="#er-det-noedvendigt" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Er det nødvendigt at udskifte et asbesttag?
                </a>
                <span className="text-stone-300">•</span>
                <a href="#autoriseret-firma" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Derfor skal du vælge et autoriseret firma
                </a>
                <span className="text-stone-300">•</span>
                <a href="#faq" className="text-stone-900 hover:text-[#BE0F12] underline">
                  Oftest stillede spørgsmål
                </a>
              </div>
            </div>
          </div>

          {/* Right Callout Box: Prisberegner & Lasse */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-stone-50 border border-stone-200 p-6 sm:p-8 space-y-4">
              <h3 className="text-lg font-bold text-stone-900 uppercase">
                Få et uforpligtende tilbud – Prisberegner: Nyt tag
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Ud fra 4 simple spørgsmål beregner vi med det samme en uforpligtende pris til dig på nyt tag. Hurtigt og nemt!
              </p>
              <Link
                to="/prisberegner"
                className="inline-block w-full text-center px-6 py-3 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors"
              >
                Prisberegner: Nyt tag
              </Link>
            </div>

            {/* Lasse Sidebar billede som på originalen */}
            <div className="flex flex-row items-end justify-center gap-2 sm:gap-3 pt-2">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/har-du-sporgsmaal.png"
                alt="Har du spørgsmål, så tag fat i mig!"
                className="w-[42%] max-w-[145px] h-auto object-contain self-center"
              />
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/lasse-sidebar.png"
                alt="Lasse Vesterhouen - Dansk Tagbyg"
                className="w-[58%] max-w-[200px] h-auto object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sektion: Pris på nedtagning af asbesttag */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
                Pris på nedtagning af asbesttag
              </h2>
              <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Prisen på nedtagning af et asbesttag afhænger af flere forskellige forhold, og derfor vil der altid være tale om en individuel vurdering. Det handler blandt andet om tagets størrelse, hvor let det er at komme til, og hvordan affaldet skal håndteres og bortskaffes.
                </p>
                <p>
                  I mange tilfælde spiller det også ind, om nedtagningen skal kombineres med et{' '}
                  <Link to="/nyt-tag" className="text-[#BE0F12] font-semibold underline hover:text-black">
                    nyt tag
                  </Link>
                  , da det kan give en mere effektiv samlet løsning. Når vi kigger på et projekt, gennemgår vi altid taget grundigt, så du får et realistisk billede af både proces og pris.
                </p>
                <p>
                  Hos Dansk Tagbyg sørger vi for en sikker og korrekt håndtering hele vejen igennem, så arbejdet bliver udført efter gældende regler og med fokus på både sikkerhed og kvalitet.
                </p>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-2_ff17e1d8.webp"
                alt="Asbest - Pris på nedtagning af asbesttag"
                className="w-full h-80 sm:h-96 object-cover border border-stone-200 shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Sådan foregår nedtagning af asbesttag */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-3_bfaf7970.webp"
                alt="Asbest - Sådan foregår nedtagning af asbesttag"
                className="w-full h-80 sm:h-96 object-cover border border-stone-200 shadow-sm"
              />
            </div>
            <div className="lg:col-span-7 space-y-4 order-1 lg:order-2">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
                Sådan foregår nedtagning af asbesttag
              </h2>
              <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Når et asbesttag skal fjernes, følger vi en fast og sikker proces, der sikrer både dig, ejendommen og vores håndværkere.
                </p>
                <p>
                  Først laver vi en grundig gennemgang af taget og vurderer opgaven. Herefter bliver området sikret og afskærmet, så asbestfibre ikke spredes til omgivelserne under arbejdet.
                </p>
                <p>
                  Selve nedtagningen foregår kontrolleret, hvor tagpladerne fjernes forsigtigt og håndteres korrekt, så materialet ikke beskadiges unødigt. Alt asbestaffald bliver derefter pakket og bortskaffet efter de gældende regler.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Er det nødvendigt at udskifte et asbesttag? */}
      <section id="er-det-noedvendigt" className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-4">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
                Er det nødvendigt at udskifte et asbesttag?
              </h2>
              <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Det er ikke farligt at bo i et hus med et asbesttag, så længe materialet er sikkert indkapslet i tagpladerne. Problemet opstår ved nedrivning, slitage eller hvis der skæres i pladerne i forbindelse med montering af eksempelvis solceller eller ovenlysvinduer. I de situationer kan de sundhedsskadelige fibre frigives til luften.
                </p>
                <p>
                  Hos Dansk Tagbyg hjælper vi gerne med at vurdere, om dit tag udgør en risiko, og om det er nødvendigt at udskifte det.
                </p>
              </div>
              <div className="pt-2">
                <a
                  href="#kontakt"
                  className="inline-block px-8 py-3.5 bg-[#BE0F12] hover:bg-[#a00d0f] text-white font-bold text-sm tracking-wider uppercase transition-colors"
                >
                  Kontakt os og få rådgivning
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-4_9588f5df.webp"
                alt="Asbest: Læs mere om kravene"
                className="w-full h-80 sm:h-96 object-cover border border-stone-200 shadow-sm"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Derfor skal du vælge et autoriseret firma */}
      <section id="autoriseret-firma" className="py-16 sm:py-20 bg-white border-b border-stone-200 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <img
                src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/scraped-5_a0952890.webp"
                alt="Asbest: Derfor skal du vælge Dansk Tagbyg"
                className="w-full h-80 sm:h-96 object-cover border border-stone-200 shadow-sm"
              />
            </div>
            <div className="lg:col-span-7 space-y-4 order-1 lg:order-2">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
                Derfor skal du vælge et autoriseret firma
              </h2>
              <div className="space-y-4 text-stone-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Hvis du som boligejer hyrer et firma uden autorisation kan det medføre bødestraf på 15.000 kr. eller mere afhængigt af overtrædelsens alvor. Derudover kan forkert håndtering af asbest føre til forurening af bygningen, hvilket kan resultere i yderligere omkostninger til oprydning og rengøring.
                </p>
                <p>
                  Det er derfor vigtigt at sikre, at arbejdet udføres af en autoriseret virksomhed. Hos Dansk Tagbyg er vi autoriseret til at håndtere asbest og sørger for, at alt foregår forsvarligt og efter gældende regler.
                </p>
              </div>
              <div className="pt-2">
                <a
                  href="#kontakt"
                  className="inline-block px-8 py-3.5 bg-stone-900 hover:bg-black text-white font-bold text-sm tracking-wider uppercase transition-colors"
                >
                  Læs mere her
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Find svar på ofte stillede spørgsmål (FAQ) */}
      <section id="faq" className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200 scroll-mt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-900 uppercase">
            Find svar på ofte stillede spørgsmål
          </h2>

          <div className="space-y-3">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq.includes(idx);
              return (
                <div key={idx} className="bg-white border border-stone-200">
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full py-4 px-6 text-left flex justify-between items-center gap-4 hover:bg-stone-50 transition-colors"
                  >
                    <span className="font-bold text-stone-900 text-base sm:text-lg">
                      {faq.q}
                    </span>
                    <span className="text-[#BE0F12] font-black text-xl shrink-0">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-5 pt-1 text-stone-700 text-sm sm:text-base leading-relaxed border-t border-stone-100">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kontakt mig, Lasse sektion */}
      <section className="py-12 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-stone-50 border border-stone-200 p-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <h3 className="text-2xl font-extrabold text-stone-900 uppercase">
                Kontakt mig, Lasse
              </h3>
              <p className="text-stone-600 text-sm mt-1">
                Har du spørgsmål om nedtagning af asbesttag eller vil du have en vurdering?
              </p>
            </div>
            <div className="flex flex-wrap gap-4 text-sm font-bold">
              <a
                href={`tel:${COMPANY_INFO.phone}`}
                className="px-6 py-3 bg-stone-900 hover:bg-black text-white transition-colors"
              >
                Ring: {COMPANY_INFO.phoneFormatted}
              </a>
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="px-6 py-3 bg-white border border-stone-300 hover:bg-stone-100 text-stone-800 transition-colors"
              >
                Skriv: {COMPANY_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bund: Anmeldelser og Kontaktformular (som på originalen) */}
      <section id="kontakt" className="py-16 sm:py-24 bg-stone-100 scroll-mt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 sm:gap-16 items-start">
            
            {/* Venstre kolonne: Firmaoplysninger og Anmeld Håndværker */}
            <div className="lg:col-span-5 space-y-8">
              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-3">
                <div className="flex items-center gap-1 text-amber-500 text-lg">
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                  <i className="fa-solid fa-star"></i>
                </div>
                <a
                  href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
                  target="_blank"
                  rel="noreferrer"
                  className="block font-black text-xl text-stone-900 hover:text-[#BE0F12] transition-colors"
                >
                  4,8 ud af 155 anmeldelser
                </a>
                <p className="text-xs text-stone-500">
                  Finalist til Årets Håndværker 5 år i træk
                </p>
              </div>

              <div className="p-6 bg-white border border-stone-200 shadow-sm space-y-4 text-sm text-stone-700">
                <h3 className="font-extrabold text-xl text-stone-900 uppercase">
                  Dansk Tagbyg ApS
                </h3>
                <div className="space-y-2">
                  <p>
                    <strong className="text-stone-900">Ring:</strong>{' '}
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Skriv:</strong>{' '}
                    <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.email}
                    </a>
                  </p>
                  <p>
                    <strong className="text-stone-900">Adr:</strong> {COMPANY_INFO.address}
                  </p>
                  <p>
                    <strong className="text-stone-900">CVR:</strong> {COMPANY_INFO.cvr}
                  </p>
                </div>
                <div className="pt-3 border-t border-stone-200">
                  <p className="font-bold text-stone-900">Vi løser opgaver i hele DK</p>
                </div>
                <div className="pt-2">
                  <p className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-2">Vi er medlem af</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <img
                      src="/_assets/swisspearl-logo.png"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = 'https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/swisspearl-logo.png';
                      }}
                      alt="Swisspearl"
                      className="h-7 sm:h-8 w-auto object-contain"
                    />
                    <img
                      src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-12-thumb.png"
                      alt="Byg Garanti"
                      className="h-6 w-auto object-contain"
                    />
                    <img
                      src="https://assets.auxodev.dk/assets/78966fd0-599e-4938-a2ab-b403c14ab21e/imgi-11-thumb.png"
                      alt="DI Byggeri"
                      className="h-6 w-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Kontaktformular */}
            <div className="lg:col-span-7 bg-white border border-stone-200 p-8 sm:p-10 shadow-sm">
              <ContactForm idPrefix="asbestos" submitButtonText="Send" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
